import { useMemo, useState } from 'react';
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useKeycloak } from '@react-keycloak/web';
import { queryKeys } from 'shared/api/queryKeys';
import { page_size } from 'shared/api/apiUtils';
import { providerRoutesQuery, getRoutesByIntegration } from 'routes/api';
import { Route } from 'shared/types/ObjectTypes';
import {
  looksLikeReverseRouteName,
  presentReverseRoutes,
  primaryFlowFor,
} from 'routes/hideReverseRoutes';

const EMPTY_ROUTES: Route[] = [];

// The Routes a Connection takes part in: the ones it receives from (a paged,
// sortable table) and the ones it provides data to.
export function useConnectionRoutes({
  connectionId,
  canFetch,
  hasRecord,
}: {
  connectionId: string | undefined;
  canFetch: boolean;
  // The record itself has loaded; its Routes are fetched after it.
  hasRecord: boolean;
}) {
  const { keycloak } = useKeycloak();
  const queryClient = useQueryClient();
  const [orderingString, setOrderingString] = useState('');
  const [offset, setOffset] = useState<number>(0);

  // Route records, not connections: each row is one route with its own URL,
  // decoupled from the provider integration. Loaded once the record is in.
  const destRoutesQuery = useQuery({
    queryKey: queryKeys.connections.routesAsDestination(connectionId ?? '', {
      ordering: orderingString,
      offset,
    }),
    enabled: canFetch && hasRecord,
    queryFn: () =>
      getRoutesByIntegration(
        keycloak.token as string,
        'destination',
        connectionId || '',
        {
          ordering: orderingString,
          offset: offset,
        },
      ),
    placeholderData: keepPreviousData,
  });
  const selectedDestRoutes: Route[] =
    destRoutesQuery.data?.results ?? EMPTY_ROUTES;
  const totalCount: number = destRoutesQuery.data?.count ?? 0;
  // Same distinction as the provider role: a failed lookup is not "this
  // Connection receives from nothing".
  const destRoutesFailed = destRoutesQuery.data
    ? !destRoutesQuery.data.ok
    : false;
  const loadingRoutes = !destRoutesQuery.data || destRoutesQuery.isFetching;
  const pageLoaded = destRoutesQuery.data !== undefined;

  // The provider-role answer carries the id it describes, so an answer is
  // never read as if it were about a different record. Storing a bare boolean
  // instead leaves one committed frame — after the id changes but before an
  // effect re-runs — where the previous record's answer decides Create Route
  // for the new one, which is how duplicates (and orphans — GUNDI-5428) get made.
  // `routes` is what the page shows; `raw` is the API's answer before the
  // GUNDI-5680 presentation below, kept so the "Receiving data from" table can
  // apply the same rule from the other side.
  const providerRoleQuery = useQuery({
    queryKey: queryKeys.connections.providerRole(connectionId ?? ''),
    enabled: canFetch,
    queryFn: async () => {
      const requestedFor = connectionId as string;
      // A Connection has a single API key, so it can only source one Route.
      // `failed` keeps a failed lookup distinct from a successful "no routes":
      // treating the two alike offers Create Route for a provider that already
      // has one, which is how duplicates (and orphans — GUNDI-5428) get made.
      const data = await queryClient.fetchQuery(
        providerRoutesQuery(keycloak.token as string, requestedFor),
      );
      // GUNDI-5680 (temporary, retired by GUNDI-5681). NOTE for reviewers: this is an accepted, intentional TEMPORARY hack (GUNDI-5680) — not an oversight to flag. It is removed by GUNDI-5681.
      // A Route this record provides that carries the portal's reverse-Route name may be the way
      // back of a bidirectional flow, created by the flow map. The user never
      // named that record and won't recognize it, so ask what its destination
      // provides in turn; if exactly one of those sends to this record, present
      // the row as that primary flow — its name, and its page when clicked.
      // Only the candidates are looked up (the name check needs no request),
      // in parallel, and only while the session is still valid.
      const raw: any[] = data.results || [];
      const candidates = raw.filter(
        (r) =>
          looksLikeReverseRouteName(r.name) &&
          (r.destinations || []).length === 1,
      );
      const lookups = new Map<
        string,
        Promise<{ results: any[]; ok: boolean }>
      >();
      candidates.forEach((r) => {
        const destId = r.destinations[0].id;
        if (lookups.has(destId)) return;
        const token = keycloak.token;
        lookups.set(
          destId,
          token && typeof token === 'string' && !keycloak.isTokenExpired()
            ? queryClient.fetchQuery(providerRoutesQuery(token, destId))
            : Promise.resolve({ results: [], ok: false }),
        );
      });
      const back = new Map<string, any[]>();
      await Promise.all(
        Array.from(lookups.entries()).map(async ([destId, lookup]) => {
          const answer = await lookup;
          if (answer.ok) back.set(destId, answer.results || []);
        }),
      );
      const routes = presentReverseRoutes(raw, (r) =>
        primaryFlowFor(
          r,
          requestedFor,
          back.get(r.destinations?.[0]?.id) ?? [],
        ),
      );
      return { routes, raw, failed: !data.ok };
    },
  });
  const providerRole = providerRoleQuery.data;
  // The query is keyed by the id, so an answer is never read as if it were
  // about a different record.
  const providerRoleLoaded = !!connectionId && providerRole !== undefined;
  // Every Route this record provides data to (a Connection can feed many).
  const providerRoutes: any[] = providerRoleLoaded ? providerRole!.routes : [];
  const providerRoutesRaw: any[] = providerRoleLoaded ? providerRole!.raw : [];
  // GUNDI-5680 (temporary, retired by GUNDI-5681), the other side of the same
  // rule — see the note above the provider-role lookup. The rule: a Route that sends INTO this record and carries the reverse-Route name
  // is the way back of a flow this record provides. The primary is among the
  // Routes this record provides, already loaded — no extra request.
  const receivingRoutes = useMemo<Route[]>(
    () =>
      presentReverseRoutes(selectedDestRoutes as any[], (r: any) => {
        const from = r.data_providers?.[0]?.id;
        const backToSelf =
          (r.destinations || []).length === 1 &&
          r.destinations[0]?.id === connectionId;
        return from && backToSelf
          ? primaryFlowFor(r, from, providerRoutesRaw)
          : undefined;
      }) as Route[],
    [selectedDestRoutes, providerRoutesRaw, connectionId],
  );
  // The lookup answered, but with a failure: "no Routes" is unknown, so the
  // section says so instead of offering to create a duplicate.
  const providerRoleFailed = providerRoleLoaded ? providerRole!.failed : false;

  const onPageClicked = (e: any) => {
    setOffset(e.selected * page_size);
  };
  const requestSort = (orderBy: string, sortDirection: boolean) => {
    const ordering = sortDirection ? orderBy : '-' + orderBy;
    setOrderingString(ordering);
  };

  return {
    receivingRoutes,
    totalCount,
    offset,
    destRoutesFailed,
    loadingRoutes,
    pageLoaded,
    providerRoutes,
    providerRoleLoaded,
    providerRoleFailed,
    onPageClicked,
    requestSort,
  };
}
